// Money, in whole cents. Every amount the app adds up — estimates, storage contracts and
// (from roadmap Phase 1) invoices and payments — goes through here, so a printed document's
// lines always add up to its total and two screens never disagree by a cent.
//
// Rules:
//   - Store and add cents (integers). Convert from dollars once, at the edge (toCents).
//   - A percentage (tax, fee, deposit) is rounded to the cent on its own, half away from zero,
//     and the total is the sum of the rounded pieces.
//   - No Firebase or DOM imports: this runs in the browser, in Cloud Functions and in
//     tests/unit as-is.

/**
 * Tax and fee rates used when Settings › Pricing (/pricing/estimateDefaults) has never been saved.
 * Same values as the estimate screens' ESTIMATE_DEFAULTS_FALLBACK, so an invoice and an estimate
 * for the same work agree. A new marina sets its own in Settings › Pricing (docs/onboarding.md).
 */
export const PRICING_DEFAULTS = Object.freeze({ salesTaxRate: 0.08625, environmentalFeeRate: 0.015 });

/**
 * Winter storage's own rates (Settings › Storage rates, /pricing/storageContract, in percent) when
 * never saved. Same as utilities.js STORAGE_DEFAULTS; storage contracts and their invoices use these.
 */
export const STORAGE_RATE_DEFAULTS = Object.freeze({ taxPct: 8.625, envPct: 2.5 });

/** Round a cent value that may carry float noise (e.g. 100.49999999999999 → 101 for 1.005 × 100). */
export function roundCents(x) {
  const n = Number(x);
  if (!Number.isFinite(n)) return 0;
  const clean = Number(n.toPrecision(12)); // drop binary-float noise before rounding
  return Math.sign(clean) * Math.round(Math.abs(clean));
}

/** Dollars (number or "$1,234.50"-style string) → integer cents. Junk → 0. */
export function toCents(dollars) {
  if (typeof dollars === 'string') dollars = dollars.replace(/[$,\s]/g, '');
  const n = Number(dollars);
  return Number.isFinite(n) ? roundCents(n * 100) : 0;
}

/** Integer cents → dollars (number), e.g. for records that store dollars. */
export const toDollars = cents => roundCents(cents) / 100;

/** Integer cents → "$1,234.50" (negative: "-$12.00"). */
export function formatMoney(cents) {
  const c = roundCents(cents);
  const s = (Math.abs(c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${c < 0 ? '-' : ''}$${s}`;
}

/** Sum of cent values. */
export const sumCents = list => (list || []).reduce((a, c) => a + roundCents(c), 0);

/** `rate` (0.08625 = 8.625%) of an amount in cents, rounded to the cent. */
export const percentOf = (cents, rate) => roundCents(roundCents(cents) * (Number(rate) || 0));

/** qty × unit price (cents), rounded to the cent. */
export const lineCents = (qty, unitCents) => roundCents((Number(qty) || 0) * roundCents(unitCents));

/**
 * Totals for a priced document. All inputs in cents.
 *   subtotal    the goods/labor before tax and fees
 *   taxBase     what sales tax applies to (default: subtotal)
 *   feeBase     what the environmental fee applies to (default: subtotal)
 *   taxRate, feeRate   e.g. 0.08625, 0.015
 *   depositPercent     e.g. 50 (of the total)
 * → { subtotal, fee, tax, total, deposit } in cents, where total = subtotal + fee + tax exactly.
 */
export function priceTotals({ subtotal, taxBase, feeBase, taxRate = 0, feeRate = 0, depositPercent = 0 }) {
  const sub = roundCents(subtotal);
  const fee = percentOf(feeBase == null ? sub : feeBase, feeRate);
  const tax = percentOf(taxBase == null ? sub : taxBase, taxRate);
  const total = sub + fee + tax;
  const deposit = percentOf(total, (Number(depositPercent) || 0) / 100);
  return { subtotal: sub, fee, tax, total, deposit };
}

/**
 * Split `totalCents` into parts proportional to `weights`, so the parts add up to the total
 * exactly (largest-remainder). E.g. a payment schedule, or one payment across several invoices.
 */
export function allocate(totalCents, weights) {
  const total = roundCents(totalCents);
  const w = (weights || []).map(x => Math.max(0, Number(x) || 0));
  const sumW = w.reduce((a, b) => a + b, 0);
  if (!w.length) return [];
  if (!sumW) return w.map((_, i) => (i === 0 ? total : 0));
  const raw = w.map(x => (total * x) / sumW);
  const parts = raw.map(x => Math.trunc(x));
  let left = total - parts.reduce((a, b) => a + b, 0);
  const order = raw.map((x, i) => [Math.abs(x - Math.trunc(x)), i]).sort((a, b) => b[0] - a[0] || a[1] - b[1]);
  const step = Math.sign(left);
  for (let k = 0; left !== 0 && k < order.length * 2; k++) {
    parts[order[k % order.length][1]] += step;
    left -= step;
  }
  return parts;
}
